import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

export interface Notification {
  id: number;
  type: string;
  titleEn: string;
  titleAr: string;
  messageEn: string;
  messageAr: string;
  redirectUrl?: string;
  isRead: boolean;
  createdAtUtc: string;
  readAtUtc?: string;
  entityType?: string;
  entityId?: number;
  priority: string;
}

export interface NotificationCount {
  unreadCount: number;
}

export interface NotificationResponse {
  notifications: Notification[];
  page: number;
  pageSize: number;
  totalCount: number;
  unreadCount?: number;
}

// Simple module-level shared store for unread count so all hook instances stay in sync
let globalUnreadCount = 0;
const unreadListeners = new Set<(n: number) => void>();
const setGlobalUnread = (n: number) => {
  globalUnreadCount = n;
  unreadListeners.forEach((fn) => {
    try { fn(n); } catch { }
  });
};

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(globalUnreadCount);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // For dropdown notifications with pagination
  const [dropdownNotifications, setDropdownNotifications] = useState<Notification[]>([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [hasMoreDropdown, setHasMoreDropdown] = useState(true);

  const fetchNotifications = useCallback(async (page = 1, pageSize = 20, isRead?: boolean) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        ...(isRead !== undefined && { isRead: isRead.toString() })
      });

      const response = await api.get<NotificationResponse>(`/notifications?${params}`);
      setNotifications(response.data.notifications);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      // Use consolidated endpoint: pageSize=0 returns only counts/metadata
      const params = new URLSearchParams({ page: '1', pageSize: '0' });
      const response = await api.get<NotificationResponse>(`/notifications?${params}`);
      if (typeof response.data.unreadCount === 'number') {
        setUnreadCount(response.data.unreadCount);
        setGlobalUnread(response.data.unreadCount);
      }
    } catch (err: any) {
      console.error('Failed to fetch unread count:', err);
    }
  }, []);

  const fetchDropdownNotifications = useCallback(async (page = 1, append = false) => {
    try {
      setDropdownLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: '5' // Show 5 notifications per page
      });

      const response = await api.get<NotificationResponse>(`/notifications?${params}`);

      if (append) {
        setDropdownNotifications(prev => [...prev, ...response.data.notifications]);
      } else {
        setDropdownNotifications(response.data.notifications);
      }

      setHasMoreDropdown(response.data.notifications.length === 5);
    } catch (err: any) {
      setError('Failed to fetch notifications');
      console.error('Failed to fetch dropdown notifications:', err);
    } finally {
      setDropdownLoading(false);
    }
  }, []);

  const loadMoreDropdownNotifications = useCallback(() => {
    if (!dropdownLoading && hasMoreDropdown) {
      const nextPage = Math.floor(dropdownNotifications.length / 5) + 1;
      fetchDropdownNotifications(nextPage, true);
    }
  }, [dropdownLoading, hasMoreDropdown, dropdownNotifications.length, fetchDropdownNotifications]);

  const markAsRead = useCallback(async (notificationId: number) => {
    try {
      // optimistic update
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, isRead: true, readAtUtc: new Date().toISOString() } : n));
      setDropdownNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, isRead: true, readAtUtc: new Date().toISOString() } : n));
      setUnreadCount(prev => {
        const next = Math.max(0, prev - 1);
        setGlobalUnread(next);
        return next;
      });

      await api.put(`/notifications/${notificationId}/read`);
    } catch (err: any) {
      console.error('Failed to mark notification as read:', err);
      fetchUnreadCount();
    }
  }, [fetchUnreadCount]);

  const markAllAsRead = useCallback(async () => {
    try {
      // optimistic update
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true, readAtUtc: new Date().toISOString() })));
      setDropdownNotifications(prev => prev.map(n => ({ ...n, isRead: true, readAtUtc: new Date().toISOString() })));
      setUnreadCount(0);
      setGlobalUnread(0);
      await api.put('/notifications/read-all');
    } catch (err: any) {
      console.error('Failed to mark all notifications as read:', err);
      fetchUnreadCount();
    }
  }, [fetchUnreadCount]);

  const deleteNotification = useCallback(async (notificationId: number) => {
    try {
      await api.delete(`/notifications/${notificationId}`);

      // Update local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));

      // Update unread count if it was unread
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.isRead) {
        setUnreadCount(prev => {
          const next = Math.max(0, prev - 1);
          setGlobalUnread(next);
          return next;
        });
      }
    } catch (err: any) {
      console.error('Failed to delete notification:', err);
    }
  }, [notifications]);

  const deleteAllNotifications = useCallback(async () => {
    try {
      await api.delete('/notifications/all');
      setNotifications([]);
      setUnreadCount(0);
      setGlobalUnread(0);
    } catch (err: any) {
      console.error('Failed to delete all notifications:', err);
    }
  }, []);

  // Auto-refresh unread count when component mounts or user returns/focuses
  useEffect(() => {
    // keep local state in sync with global store
    const listener = (n: number) => setUnreadCount(n);
    unreadListeners.add(listener);
    // initial sync
    setUnreadCount(globalUnreadCount);

    fetchUnreadCount();
    const onFocus = () => fetchUnreadCount();
    const onVisibility = () => { if (!document.hidden) fetchUnreadCount(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      unreadListeners.delete(listener);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    // Dropdown pagination
    dropdownNotifications,
    dropdownLoading,
    hasMoreDropdown,
    fetchDropdownNotifications,
    loadMoreDropdownNotifications,
  };
}